import {
  PreloadedState,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'

import appConfig from '@store/actionSlices/appConfig'
import bedConfig from '@store/actionSlices/bedConfig'
import blockLevel from '@store/actionSlices/blockLevel'
import firestore from '@store/actionSlices/firestore'
import houseAndLand from '@store/actionSlices/houseAndLand'
import lotFilter from '@store/actionSlices/lotFilter'
import multiScreen from '@store/actionSlices/multiScreen'
import projectIdentity from '@store/actionSlices/projectIdentity'
import shortlist from '@store/actionSlices/shortlist'
import showcaseRoom from '@store/actionSlices/showcase-room'
import statusIndicator from '@store/actionSlices/statusIndicator'
import token from '@store/actionSlices/token'
import user from '@store/actionSlices/user'
import whiteLabel from '@store/actionSlices/whiteLabel'

import { agentApi } from '@api/agent'
import { userApi } from '@api/authentication'
import { bedConfigApi } from '@api/bed-config'
import { brochureApi } from '@api/brochure'
import { buildingApi } from '@api/building'
import { clearCacheApi } from '@api/clear-cache'
import { configApi } from '@api/config'
import { externalLinksApi } from '@api/external-links'
import { externalViewsApi } from '@api/external-views'
import { floorPlateGalleryApi } from '@api/floor-plate-gallery'
import { galleryApi } from '@api/gallery'
import { generalExternalLinksApi } from '@api/general-external-links'
import { houseAndLandApi } from '@api/houseAndLand'
import { interactiveMapApi } from '@api/interactive-map'
import { interactivePlanApi } from '@api/interactive-plan'
import { locationApi } from '@api/location'
import { masterKeyApi } from '@api/master-key'
import { panoramicApi } from '@api/panoramic'
import { projectListApi } from '@api/project-list'
import { shortlistedApi } from '@api/shortlisted'
import { showcase3dApi } from '@api/showcase-3d'
import { showcaseRoomApi } from '@api/showcase-room'
import { teamApi } from '@api/team'
import { unitGalleryApi } from '@api/unit-gallery'
import { videoGalleryApi } from '@api/video-gallery'
import { visionApi } from '@api/vision'
import { whiteLabelApi } from '@api/white-label'

const initialState = {}

const rootReducer = combineReducers({
  user,
  token,
  projectIdentity,
  shortlist,
  bedConfig,
  blockLevel,
  statusIndicator,
  appConfig,
  houseAndLand,
  firestore,
  whiteLabel,
  lotFilter,
  showcaseRoom,
  multiScreen,
  [userApi.reducerPath]: userApi.reducer,
  [buildingApi.reducerPath]: buildingApi.reducer,
  [configApi.reducerPath]: configApi.reducer,
  [shortlistedApi.reducerPath]: shortlistedApi.reducer,
  [bedConfigApi.reducerPath]: bedConfigApi.reducer,
  [panoramicApi.reducerPath]: panoramicApi.reducer,
  [interactivePlanApi.reducerPath]: interactivePlanApi.reducer,
  [videoGalleryApi.reducerPath]: videoGalleryApi.reducer,
  [externalViewsApi.reducerPath]: externalViewsApi.reducer,
  [galleryApi.reducerPath]: galleryApi.reducer,
  [brochureApi.reducerPath]: brochureApi.reducer,
  [locationApi.reducerPath]: locationApi.reducer,
  [interactiveMapApi.reducerPath]: interactiveMapApi.reducer,
  [visionApi.reducerPath]: visionApi.reducer,
  [teamApi.reducerPath]: teamApi.reducer,
  [projectListApi.reducerPath]: projectListApi.reducer,
  [floorPlateGalleryApi.reducerPath]: floorPlateGalleryApi.reducer,
  [unitGalleryApi.reducerPath]: unitGalleryApi.reducer,
  [masterKeyApi.reducerPath]: masterKeyApi.reducer,
  [houseAndLandApi.reducerPath]: houseAndLandApi.reducer,
  [externalLinksApi.reducerPath]: externalLinksApi.reducer,
  [generalExternalLinksApi.reducerPath]: generalExternalLinksApi.reducer,
  [whiteLabelApi.reducerPath]: whiteLabelApi.reducer,
  [showcaseRoomApi.reducerPath]: showcaseRoomApi.reducer,
  [agentApi.reducerPath]: agentApi.reducer,
  [clearCacheApi.reducerPath]: clearCacheApi.reducer,
  [showcase3dApi.reducerPath]: showcase3dApi.reducer,
  ...initialState,
})

export type RootStateType = ReturnType<typeof rootReducer>

export function setupStore(
  preloadedState?: PreloadedState<ReturnType<typeof rootReducer>>
) {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        configApi.middleware,
        userApi.middleware,
        buildingApi.middleware,
        shortlistedApi.middleware,
        bedConfigApi.middleware,
        panoramicApi.middleware,
        interactivePlanApi.middleware,
        videoGalleryApi.middleware,
        externalViewsApi.middleware,
        galleryApi.middleware,
        brochureApi.middleware,
        locationApi.middleware,
        interactiveMapApi.middleware,
        teamApi.middleware,
        visionApi.middleware,
        floorPlateGalleryApi.middleware,
        unitGalleryApi.middleware,
        projectListApi.middleware,
        masterKeyApi.middleware,
        houseAndLandApi.middleware,
        externalLinksApi.middleware,
        generalExternalLinksApi.middleware,
        whiteLabelApi.middleware,
        showcaseRoomApi.middleware,
        agentApi.middleware,
        clearCacheApi.middleware,
        showcase3dApi.middleware
      ),
    preloadedState,
  })
}

const store = setupStore()

setupListeners(store.dispatch)

export type RootState = ReturnType<typeof rootReducer>

export type AppDispatchType = typeof store.dispatch

export type AppStore = ReturnType<typeof setupStore>

export default { store }
